"use client";

import { LandingHeader } from "./LandingHeader";
import { HeroSection } from "./HeroSection";
import { DemoVideoSection } from "./DemoVideoSection";
import { AlignmentLoopShowcase } from "./AlignmentLoopShowcase";
import { RealitySection } from "./RealitySection";
import { JournalSection } from "./JournalSection";
import { AssistantSection } from "./AssistantSection";
import { LanguageSection } from "./LanguageSection";
import { ToolkitSection } from "./ToolkitSection";
import { HowItWorksSection } from "./HowItWorksSection";
import { ScrollPauseController } from "./ScrollPauseController";
import { Footer } from "./Footer";
import type { StoryCard } from "./StoriesSection";
import { useTranslation } from "@/lib/i18n/context";

interface LandingContentProps {
  session: { user: { name?: string | null } } | null;
  /** Latest blog posts for the homepage "Stories" section (locale-matched). */
  stories?: StoryCard[];
}

export function LandingContent({ session, stories = [] }: LandingContentProps) {
  const { t } = useTranslation();
  const edge = t.landing.cover;

  return (
    <div className="min-h-screen bg-background paper-grain relative pb-[calc(120px+env(safe-area-inset-bottom))] md:pb-0">
      <span
        className="hidden lg:block fixed top-[120px] left-3 z-[3] font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)] opacity-70 pointer-events-none"
        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
      >
        {edge.edgeLeft}
      </span>
      <span
        className="hidden lg:block fixed top-[120px] right-3 z-[3] font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)] opacity-70 pointer-events-none"
        style={{ writingMode: "vertical-rl" }}
      >
        {edge.edgeRight}
      </span>

      <ScrollPauseController />
      <LandingHeader session={session} />
      {/* `<main>` landmark wraps the primary content so screen-reader users
          can skip the nav and jump straight here. Header (banner) and Footer
          (contentinfo) stay outside, each its own landmark. */}
      <main>
        <HeroSection session={session} />
        {/* Branded click-to-play product demo — sits directly under the hero.
            Facade makes no external request until the user clicks play. */}
        <DemoVideoSection />
        {/* The Reality scroll-pause marker now lives INSIDE the section, right
            before the demo (see RealityDemo), so the pin lands on the animation
            rather than the section heading. */}
        <RealitySection />
        {/* Scroll target for the hero's "See how it works" — offset for the
            sticky 72px header via scroll-margin-top. */}
        <div id="how-it-works" className="scroll-mt-[72px]">
          <AlignmentLoopShowcase />
        </div>
        <JournalSection />
        <AssistantSection />
        <LanguageSection />
        <ToolkitSection />
        <HowItWorksSection stories={stories} />
      </main>
      {/* External "Featured on" badges — sit at the very bottom of the page,
          just above (and outside) the footer. */}
      <div className="flex flex-wrap items-center justify-center gap-4 pt-10 pb-4">
        <a
          href="https://betalist.com/startups/journail?utm_campaign=badge-journail&utm_medium=badge&utm_source=badge-featured"
          target="_blank"
          rel="noopener noreferrer"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="https://betalist.com/badges/featured?id=171615&theme=gray"
            alt="Journail - Goals-first daily planner and journal with AI assistant in 5 minutes | BetaList"
            width={156}
            height={54}
            style={{ width: 156, height: 54 }}
          />
        </a>
        <a href="https://peerlist.io/journail/project/journail" target="_blank" rel="noreferrer">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="https://peerlist.io/api/v1/projects/embed/PRJHP6LPJ77L9J7EJFQ66KMANLL9DP?showUpvote=true&theme=light"
            alt="Journail"
            width={240}
            height={72}
            style={{ width: "auto", height: 72 }}
          />
        </a>
        <a href="https://peerpush.com/p/journail" target="_blank" rel="noopener noreferrer">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="https://peerpush.com/p/journail/badge.png"
            alt="Journail on PeerPush"
            width={230}
            height={54}
            style={{ width: 230, height: "auto" }}
          />
        </a>
        <a href="https://launchstag.com" target="_blank" rel="noopener noreferrer">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="https://launchstag.com/badge-light.svg"
            alt="Featured on Launchstag"
            width={198}
            height={62}
          />
        </a>
        <a
          href="https://lift-off.sh/p/journail"
          target="_blank"
          rel="noopener noreferrer"
          title="LiftOff launch badge"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="https://lift-off.sh/images/badges/badgeLaunchedLight.webp"
            alt="LiftOff launch badge"
            width={200}
            height={62}
            style={{ height: "auto" }}
          />
        </a>
        <a target="_blank" rel="noopener noreferrer" href="https://goodaitools.com/ai/journail">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="https://goodaitools.com/assets/images/badge.png"
            alt="Good AI Tools"
            width={174}
            height={54}
            loading="lazy"
            style={{ height: 54, width: "auto" }}
          />
        </a>
        <a href="https://tools.cafe" target="_blank" rel="noopener noreferrer">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="https://tools.cafe/b/light.svg"
            alt="Featured on tools.cafe"
            width={256}
            height={80}
          />
        </a>
      </div>
      <Footer />
    </div>
  );
}
